<template>
  <div v-if="hasActions" class="element-actions" :style="actionsStyle">
    <template v-for="(item, index) in groupedActions" :key="`action-${index}`">
      <!-- Single action -->
      <MAction v-if="item.type === 'single'" :data="item.action" :registrable="true" />

      <!-- Grouped actions with dropdown -->
      <div v-else-if="item.type === 'group'" class="action-group">
        <MAction :data="item.mainAction" :registrable="true" :in-group="true" class="main-action" />
        <Button icon="pi pi-chevron-down" size="small" class="action-dropdown-toggle"
          @click="toggleDropdown($event, index)" aria-label="Показать дополнительные действия" />
        <Menu :ref="(el: MenuInstance | null) => setMenuRef(el, index)" :model="getDropdownItems(item.actions)"
          :popup="true" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { usePageData } from '@/common/composables/usePageData'
import { useActionRoleAccess } from '@/common/composables/useActionRoleAccess'
import {
  filterElementLayoutActions,
  getActionDropDownName,
  isActionDropDownMain,
  isElementLayoutActionTargetVisible,
  sortActionsByLayoutSort,
} from '@/common/utils/elementLayoutActions'
import MAction from '@/components/platform/MAction.vue'
import { FormProviderKey } from '@/components/platform/injection-keys'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import { computed, ref, inject } from 'vue'
import { getPrimeIcon } from '@/common/utils/iconMapper'
import { useDropdownGroupActionRegistry } from '@/common/composables/useDropdownGroupActionRegistry'

interface Props {
  config?: any
  position: string[]
  elementId?: number
  targetElementId?: string
  level?: string
}

const props = withDefaults(defineProps<Props>(), {
  config: () => ({}),
  position: () => [],
  elementId: -1,
  targetElementId: '',
  level: 'element'
})

const { pageActions, pageRegistryFormId } = usePageData()
const { hasActionRoleAccess } = useActionRoleAccess()
const panelId = inject<string>('panelId', '')
const formProvider = inject(FormProviderKey, null)

const actions = computed(() => {
  const targetId = props.targetElementId || props.elementId?.toString()
  const targetLevel = props.level || 'element'
  const shellMap = formProvider?.elementShellVisibleByElementId?.value
  if (!isElementLayoutActionTargetVisible(targetId, shellMap)) {
    return []
  }

  return filterElementLayoutActions(pageActions.value, {
    targetElementId: targetId,
    level: targetLevel,
    positions: props.position,
  }).filter((action) => hasActionRoleAccess(action.roles))
})

// Group actions by dropDownActionName, collecting from ALL positions globally
const groupedActions = computed(() => {
  const sorted = sortActionsByLayoutSort(actions.value)

  // Build global groups map from ALL page actions for dropdown groups
  const globalGroups: Map<string, any[]> = new Map()

  pageActions.value.forEach(action => {
    const dropDownName = getActionDropDownName(action)
    if (dropDownName !== '') {
      if (!globalGroups.has(dropDownName)) {
        globalGroups.set(dropDownName, [])
      }
      globalGroups.get(dropDownName)!.push(action)
    }
  })

  // Now process only the filtered actions for this position
  const groups: Map<string, any[]> = new Map()
  const ungrouped: any[] = []
  const processedGroups = new Set<string>()

  sorted.forEach(action => {
    const dropDownName = getActionDropDownName(action)
    const hasDropDownGroup = dropDownName !== ''

    if (hasDropDownGroup && !processedGroups.has(dropDownName)) {
      processedGroups.add(dropDownName)

      // Use the global group data
      const groupActions = globalGroups.get(dropDownName) || []
      const mainAction = groupActions.find(a => isActionDropDownMain(a))

      // Only render if the current action is the main action
      if (action === mainAction || (!mainAction && action === groupActions[0])) {
        groups.set(dropDownName, groupActions)
      }
    } else if (!hasDropDownGroup) {
      ungrouped.push(action)
    }
  })

  // Create final list
  const result: any[] = []

  // Add ungrouped actions
  ungrouped.forEach(action => {
    result.push({ type: 'single', action })
  })

  // Add grouped actions
  groups.forEach((groupActions, groupName) => {
    const mainAction = groupActions.find(a => isActionDropDownMain(a))

    result.push({
      type: 'group',
      groupName,
      mainAction: mainAction || groupActions[0],
      actions: groupActions
    })
  })

  return result
})

const hasActions = computed(() => groupedActions.value.length > 0)

// Menu refs management
type MenuInstance = InstanceType<typeof Menu>
const menuRefs = ref<Map<number, MenuInstance>>(new Map())

const setMenuRef = (el: MenuInstance | null, index: number) => {
  if (el) {
    menuRefs.value.set(index, el)
  }
}

const toggleDropdown = (event: Event, index: number) => {
  const menu = menuRefs.value.get(index)
  if (menu) {
    menu.toggle(event)
  }
}

// Get onAction from injection
const onAction = inject<((context: any) => void)>('onAction', () => { })

const executeDropdownAction = async (action: any) => {
  const ctx = {
    id: action._attributes ? +action._attributes.id : 0,
    name: action.name?._text || '',
    elementId: props.targetElementId || props.elementId?.toString(),
    level: props.level,
    data: action
  }

  await onAction({ getContext: () => ctx })
}

const registryFormId = computed(() => {
  if (pageRegistryFormId.value) {
    const n = parseInt(pageRegistryFormId.value, 10)
    if (Number.isFinite(n)) return n
  }
  return 0
})

const targetElementId = computed(
  () => props.targetElementId || props.elementId?.toString() || '',
)

const { isDropdownMenuActionVisible, isDropdownMenuActionEnabled } = useDropdownGroupActionRegistry({
  groupedActions,
  formId: registryFormId,
  elementId: targetElementId,
  panelId,
  onActivateDropdown: executeDropdownAction,
})

// Format actions for PrimeVue Menu
const getDropdownItems = (actions: any[]) => {
  return actions
    .filter((action) => isDropdownMenuActionVisible(action))
    .map(action => ({
    label: action.title?._text || action.name?._text || 'Action',
    icon: getPrimeIcon(action.image?._text || ''),
    disabled: !isDropdownMenuActionEnabled(action),
    command: () => executeDropdownAction(action)
  }))
}

const actionsStyle = computed(() => {
  const isTop = props.position.includes('top')
  const isBottom = props.position.includes('bottom')
  const isLeft = props.position.includes('left')
  return {
    justifyContent: isLeft || isBottom ? 'flex-start' : 'flex-end',
    width: isTop || isBottom ? '100%' : 'auto',
    flexWrap: isTop || isBottom ? 'nowrap' : undefined,
  }
})
</script>

<style lang="scss" scoped>
.element-actions {
  display: flex;
  gap: 0.5rem;

  // Fix text wrapping for all action buttons
  :deep(.m-button .m-label__text) {
    white-space: nowrap !important;
  }
}

.element-actions.element-actions-right {
  align-items: stretch;
  gap: 0 !important;
  flex-wrap: nowrap;
  min-height: 0;
  height: 100%;
  margin: 0;

  > .action,
  > .action-group {
    display: flex;
    align-items: stretch;
    height: 100%;
    min-height: 0;
    margin: 0;
    flex-shrink: 0;
  }

  > .action :deep(.p-button),
  > .action-group :deep(.p-button) {
    height: 100% !important;
    margin: 0 !important;
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    flex-shrink: 0 !important;
  }

  /* Swing: labeled right-action keeps intrinsic width (icon + title), never icon-square */
  > .action :deep(.p-button:has(.m-label__text)),
  > .action-group :deep(.p-button:has(.m-label__text)) {
    width: auto !important;
    min-width: unset !important;
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
    white-space: nowrap !important;
  }

  > .action:not(:last-child) :deep(.p-button),
  > .action-group:not(:last-child) :deep(.p-button) {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-right: 1px solid #d1d5db !important;
  }

  > .action:last-child :deep(.p-button),
  > .action-group:last-child :deep(.p-button) {
    border-top-right-radius: 8px !important;
    border-bottom-right-radius: 8px !important;
  }
}

.element-actions.element-actions-right.element-actions-right--split {
  align-items: center;
  gap: 8px !important;
  height: auto;
  min-height: 0;

  > .action,
  > .action-group {
    align-items: center;
    height: auto;
  }

  > .action :deep(.p-button),
  > .action-group :deep(.p-button) {
    width: 2.5rem !important;
    height: 2.5rem !important;
    min-width: 2.5rem !important;
    min-height: 2.5rem !important;
    padding: 0 !important;
    border-radius: 8px !important;
    background: #fff !important;
    background-color: #fff !important;
    color: var(--primary-color) !important;
    border: 1px solid color-mix(in srgb, var(--primary-color) 20%, #e8e8e8) !important;
    box-shadow: none !important;
  }

  > .action :deep(.p-button:not(:disabled):hover),
  > .action-group :deep(.p-button:not(:disabled):hover) {
    background: color-mix(in srgb, var(--primary-color) 10%, #fff) !important;
    background-color: color-mix(in srgb, var(--primary-color) 10%, #fff) !important;
    border-color: color-mix(in srgb, var(--primary-color) 35%, #e8e8e8) !important;
  }

  > .action :deep(.m-label),
  > .action :deep(.m-label__icon) {
    color: var(--primary-color) !important;
  }

  > .action :deep(.p-button:has(.m-label__text)),
  > .action-group :deep(.p-button:has(.m-label__text)) {
    width: auto !important;
    min-width: unset !important;
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
    background: var(--primary-color) !important;
    background-color: var(--primary-color) !important;
    color: #fff !important;
    border: 1px solid color-mix(in srgb, var(--primary-color) 85%, black) !important;
  }

  > .action:not(:last-child) :deep(.p-button),
  > .action-group:not(:last-child) :deep(.p-button),
  > .action:last-child :deep(.p-button),
  > .action-group:last-child :deep(.p-button) {
    border-radius: 8px !important;
    border-right-color: color-mix(in srgb, var(--primary-color) 20%, #e8e8e8) !important;
  }
}

.action-group {
  display: inline-flex;
  align-items: stretch;
  gap: 0;
  height: 100%;
  min-height: 40px;

  .main-action {
    :deep(.p-button) {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      border-right: none !important;
      margin: 0 !important;
      height: 100%;
    }
  }
}

// Dropdown toggle button - very specific selectors to override PrimeVue
.action-dropdown-toggle {
  border-radius: 0 !important;
  border-top-right-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  border: 1px solid var(--primary-color) !important;
  border-left: 1px solid rgba(255, 255, 255, 0.3) !important;
  min-width: auto !important;
  width: 2rem !important;
  height: 100% !important;
  padding: 0 !important;
}

.action-dropdown-toggle,
.action-dropdown-toggle.p-button,
.action-dropdown-toggle.p-button.p-button-sm {
  background: var(--primary-color) !important;
  background-color: var(--primary-color) !important;
  color: white !important;

  .p-button-icon {
    font-size: 0.75rem !important;
    color: white !important;
  }

  .p-button-label {
    display: none;
  }
}

.action-dropdown-toggle:hover,
.action-dropdown-toggle.p-button:hover,
.action-dropdown-toggle.p-button.p-button-sm:hover,
.action-dropdown-toggle:enabled:hover,
.action-dropdown-toggle.p-button:enabled:hover,
.action-dropdown-toggle:not(:disabled):hover,
.action-dropdown-toggle.p-button:not(:disabled):hover {
  background: var(--primary-600) !important;
  background-color: var(--primary-600) !important;
  color: white !important;
  border-color: var(--primary-600) !important;

  .p-button-icon {
    color: white !important;
  }
}

.action-dropdown-toggle:focus,
.action-dropdown-toggle.p-button:focus {
  box-shadow: none !important;
  background: var(--primary-color) !important;
  background-color: var(--primary-color) !important;
}
</style>
